/**
 * Hedita API Client - Centralized Supabase Access
 * SINGLE SOURCE OF TRUTH for all database operations
 * All queries include user_id filter for security
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './database';

// Environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Validate configuration
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Supabase configuration missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// Create Supabase client with enhanced options
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'X-Client-Info': 'hedita@2.0.0',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Export auth helpers
export const auth = supabase.auth;

// Current user cache
let currentUserId: string | null = null;

/**
 * Get current authenticated user
 */
export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Error getting current user:', error);
    return null;
  }
  return data?.user || null;
}

/**
 * Get current user ID (cached)
 */
export async function getCurrentUserId(): Promise<string | null> {
  if (currentUserId) return currentUserId;
  const user = await getCurrentUser();
  currentUserId = user?.id || null;
  return currentUserId;
}

/**
 * Clear user cache (call on logout)
 */
export function clearUserCache() {
  currentUserId = null;
}

/**
 * Get current session
 */
export async function getCurrentSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    return null;
  }
  return data?.session || null;
}

/**
 * Type-safe query builder with automatic user_id filtering
 * ALL queries must use this to ensure data security
 */
export function createQueryBuilder<T extends keyof Database['public']['Tables']>(
  table: T,
  userId?: string
) {
  const builder = supabase.from(table as string);
  
  // Auto-filter by user_id if provided
  if (userId) {
    return builder.eq('user_id', userId);
  }
  
  return builder;
}

/**
 * Secure query helper - automatically injects user_id filter
 * Use this for ALL queries to ensure RLS enforcement
 */
export async function secureQuery<T extends keyof Database['public']['Tables']>(
  table: T,
  userId: string,
  options?: {
    select?: string;
    filters?: Record<string, unknown>;
    order?: { column: string; ascending?: boolean };
    limit?: number;
    single?: boolean;
  }
) {
  let query = supabase
    .from(table as string)
    .select(options?.select || '*')
    .eq('user_id', userId);

  // Apply additional filters
  if (options?.filters) {
    Object.entries(options.filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  }

  // Apply limit
  if (options?.limit) {
    query = query.limit(options.limit);
  }

  // Single result
  if (options?.single) {
    const { data, error } = await query.single();
    return { data, error };
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Secure insert with user_id enforcement
 */
export async function secureInsert<T extends keyof Database['public']['Tables']>(
  table: T,
  userId: string,
  data: Record<string, unknown>
) {
  const { data: result, error } = await supabase
    .from(table as string)
    .insert({ ...data, user_id: userId })
    .select()
    .single();

  return { data: result, error };
}

/**
 * Secure update with user_id verification
 */
export async function secureUpdate<T extends keyof Database['public']['Tables']>(
  table: T,
  userId: string,
  id: string,
  data: Record<string, unknown>
) {
  const { data: result, error } = await supabase
    .from(table as string)
    .update(data)
    .eq('id', id)
    .eq('user_id', userId) // Extra safety check
    .select()
    .single();

  return { data: result, error };
}

/**
 * Secure delete with user_id verification
 */
export async function secureDelete<T extends keyof Database['public']['Tables']>(
  table: T,
  userId: string,
  id: string
) {
  const { error } = await supabase
    .from(table as string)
    .delete()
    .eq('id', id)
    .eq('user_id', userId); // Extra safety check

  return { error };
}

// Realtime subscription helper
export function subscribeToTable<T extends keyof Database['public']['Tables']>(
  table: T,
  callback: (payload: unknown) => void,
  filter?: string
) {
  const channel = supabase
    .channel(`${table}_changes`)
    .on(
      'postgres_changes' as const,
      {
        event: '*',
        schema: 'public',
        table: String(table),
        filter: filter,
      },
      callback
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export default supabase;
